import { useCallback, useEffect, useState } from 'react';

import { getLocalStorageParsedValue, setOrReplaceLocalStorageItem } from '../../../utils/localStorageUtils';

/**
 * Local storage key prefixes for the StoryView sidebars.
 * Each sidebar persists its own collapsed state, scoped per user, so that
 * collapsing one sidebar never changes the other.
 */
export const STORY_OVERVIEW_SIDEBAR_STORAGE_KEY_PREFIX = 'story-overview-sidebar';
export const STORY_PAGE_LAYOUT_PROPOSALS_SIDEBAR_STORAGE_KEY_PREFIX = 'story-page-layout-proposals-sidebar';

const COLLAPSED_ATTRIBUTE = 'isCollapsed';

/**
 * Builds the user scoped local storage key for a sidebar.
 * Returns null when there is no user to scope the key to, in which case the
 * collapsed state is kept in memory only instead of being shared across users.
 */
export const buildSidebarStorageKey = (prefix: string, userId?: number): string | null =>
  userId ? `${prefix}-${userId}` : null;

const readCollapsedState = (storageKey: string | null): boolean => {
  if (!storageKey || typeof window === 'undefined') return false;
  const storedValue = getLocalStorageParsedValue(storageKey);
  return storedValue?.[COLLAPSED_ATTRIBUTE] === true;
};

export type SidebarCollapsedState = {
  isCollapsed: boolean;
  toggleCollapsed(): void;
};

/**
 * Keeps a sidebar's collapsed state in local storage under its own key,
 * scoped to the current user.
 */
export const useSidebarCollapsedState = (prefix: string, userId?: number): SidebarCollapsedState => {
  const storageKey = buildSidebarStorageKey(prefix, userId);
  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => readCollapsedState(storageKey));

  // Re-read when the key changes, e.g. when userId resolves after the first render.
  useEffect(() => {
    setIsCollapsed(readCollapsedState(storageKey));
  }, [storageKey]);

  const toggleCollapsed = useCallback(() => {
    setIsCollapsed((previousIsCollapsed) => {
      const nextIsCollapsed = !previousIsCollapsed;
      if (storageKey) setOrReplaceLocalStorageItem(storageKey, { [COLLAPSED_ATTRIBUTE]: nextIsCollapsed });
      return nextIsCollapsed;
    });
  }, [storageKey]);

  return { isCollapsed, toggleCollapsed };
};
